import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import WorkSchedulePage from './pages/WorkSchedulePage';
import useAppStore from './store/useAppStore';
import useUndoRedo from './hooks/useUndoRedo';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
import { AuthConfigProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Create theme function
const createAppTheme = (darkMode) => {
  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#6366f1' : '#4f46e5', // Indigo
        light: darkMode ? '#818cf8' : '#6366f1',
        dark: darkMode ? '#4338ca' : '#3730a3',
      },
      secondary: {
        main: darkMode ? '#ec4899' : '#e11d48', // Pink/Rose
        light: darkMode ? '#f472b6' : '#f43f5e',
        dark: darkMode ? '#be185d' : '#be123c',
      },
      success: {
        main: darkMode ? '#22c55e' : '#16a34a', // Green
        light: darkMode ? '#4ade80' : '#22c55e',
        dark: darkMode ? '#15803d' : '#15803d',
      },
      warning: {
        main: darkMode ? '#f59e0b' : '#d97706', // Amber
        light: darkMode ? '#fbbf24' : '#f59e0b',
        dark: darkMode ? '#b45309' : '#92400e',
      },
      error: {
        main: darkMode ? '#ef4444' : '#dc2626', // Red
        light: darkMode ? '#f87171' : '#ef4444',
        dark: darkMode ? '#b91c1c' : '#991b1b',
      },
      background: {
        default: darkMode ? '#0f172a' : '#f8fafc', // Slate
        paper: darkMode ? '#1e293b' : '#ffffff',
      },
      text: {
        primary: darkMode ? '#f1f5f9' : '#0f172a',
        secondary: darkMode ? '#94a3b8' : '#64748b',
      },
      divider: darkMode ? '#334155' : '#e2e8f0',
      action: {
        hover: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        selected: darkMode ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.08)',
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h6: {
        fontWeight: 600,
        fontSize: 'clamp(1rem, 2vw, 1.25rem)', // Responsive font size
      },
      body1: {
        fontSize: 'clamp(0.875rem, 1.5vw, 1rem)', // Responsive body text
      },
      body2: {
        fontSize: 'clamp(0.75rem, 1.25vw, 0.875rem)', // Responsive smaller text
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: 'clamp(8px, 2vw, 24px)',
            paddingRight: 'clamp(8px, 2vw, 24px)',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: 'clamp(4px, 1vw, 12px)',
            borderBottom: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
            '@media (max-width: 600px)': {
              padding: '4px 6px',
              fontSize: '0.75rem',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: 'clamp(0.625rem, 1.25vw, 0.75rem)',
            fontWeight: 500,
            '@media (max-width: 600px)': {
              height: '24px',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: darkMode ? '#1e293b' : '#ffffff',
            boxShadow: darkMode 
              ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
              : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            '@media (max-width: 600px)': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#1e293b' : '#4f46e5',
            color: '#ffffff',
            boxShadow: darkMode 
              ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
              : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#334155' : '#f1f5f9',
          },
          indicator: {
            backgroundColor: darkMode ? '#6366f1' : '#4f46e5',
            height: 3,
            borderRadius: '1.5px 1.5px 0 0',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
            color: darkMode ? '#94a3b8' : '#64748b',
            minWidth: 'clamp(60px, 15vw, 90px)',
            padding: 'clamp(8px, 2vw, 12px) clamp(12px, 3vw, 16px)',
            '@media (max-width: 600px)': {
              fontSize: '0.75rem',
              minWidth: '60px',
              padding: '8px',
            },
            '&.Mui-selected': {
              color: darkMode ? '#6366f1' : '#4f46e5',
              fontWeight: 600,
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 8,
            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
            padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 2vw, 16px)',
            '@media (max-width: 600px)': {
              minWidth: '44px',
              minHeight: '44px',
              fontSize: '0.75rem',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: 'clamp(6px, 1.5vw, 8px)',
            '@media (max-width: 600px)': {
              padding: '8px',
              minWidth: '44px',
              minHeight: '44px',
            },
          },
        },
      },
    },
  });
  
  return theme;
};

function App() {
  const { darkMode } = useAppStore();
  const theme = React.useMemo(() => createAppTheme(darkMode), [darkMode]);
  
  // Set data-theme attribute for CSS custom properties (scrollbars)
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);
  
  // Initialize undo/redo functionality
  useUndoRedo();
  
  // Initialize keyboard shortcuts
  useKeyboardShortcuts();

  return (
    <AuthConfigProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="App">
          <ProtectedRoute>
            <Routes>
              <Route path="/" element={<Navigate to="/kalendarz" replace />} />
              <Route path="/kalendarz" element={<WorkSchedulePage />} />
              <Route path="/urlopy" element={<WorkSchedulePage />} />
              <Route path="/pracownicy" element={<WorkSchedulePage />} />
              <Route path="*" element={<Navigate to="/kalendarz" replace />} />
            </Routes>
          </ProtectedRoute>
        </div>
      </ThemeProvider>
    </AuthConfigProvider>
  );
}

export default App;